import type { Entity } from './components'
import { T } from './tunables'

// The great unification (amoeba-unification.md): the evolution ladder IS the
// permanent economy. Tiers 2→6 are the only lasting DNA sink; each tier is a
// body change, the soft-gate key to deeper water, and unlocks new adaptations
// into the growth-spurt card pool (powers.ts tierRequired).

export type Tier = 1 | 2 | 3 | 4 | 5 | 6
export const MAX_TIER: Tier = 6

// Per-tier ladder: cost to REACH this tier, and the cumulative radius bonus
// once settled at it. Costs escalate so carried DNA (and the souls-drop)
// stays meaningful at every stage of the game.
export const TIER_SPECS: Record<Tier, { cost: number; radiusBonus: number }> = {
  1: { cost: 0,    radiusBonus: 0 },
  2: { cost: 450,  radiusBonus: 0.30 },
  3: { cost: 1000, radiusBonus: 0.55 },
  4: { cost: 2200, radiusBonus: 0.85 },
  5: { cost: 4800, radiusBonus: 1.20 },
  6: { cost: 9999, radiusBonus: 1.60 },
}

// Tier visual uniforms (red ink-mix, horns, tail, pitchfork — the descent
// toward Heck) follow the documented contract below. Tier 3 is now reachable
// and grows vestigial horns; higher tiers' distinguishing shader features are
// still being filled in, and each tier-up replays the morph fanfare.
//
//   tier 2: red ink-mix 0.10, no horns, no tail, no pitchfork
//   tier 3: red ink-mix 0.20, vestigial horns 0.4
//   tier 4: red ink-mix 0.30, horns 0.7, tail 0.5
//   tier 5: red ink-mix 0.40, horns 0.8, tail 0.7, clipboard pseudopod 1.0
//   tier 6: red ink-mix 0.55, horns 1.0, tail 1.0, pitchfork pseudopod 1.0

export function currentTier(player: Entity): Tier {
  return player.evolution?.tier ?? 1
}

export function nextTier(player: Entity): Tier | null {
  const t = currentTier(player)
  return t >= MAX_TIER ? null : ((t + 1) as Tier)
}

export function canEvolve(
  player: Entity,
  dna: number,
): { ok: true; next: Tier; cost: number } | { ok: false; reason: 'max-tier' | 'not-enough-dna'; next: Tier | null; cost: number } {
  const next = nextTier(player)
  if (next === null) return { ok: false, reason: 'max-tier', next: null, cost: 0 }
  const cost = TIER_SPECS[next].cost
  if (dna < cost) return { ok: false, reason: 'not-enough-dna', next, cost }
  return { ok: true, next, cost }
}

// Anticipation / burst / settle phasing — see T.evolution. The morph curve goes:
//   0–anticipationEnd:   ease-in dip to -anticipationDepth (body inhales)
//   anticipationEnd–burstEnd: ease-out from dip to overshoot (body bursts past target)
//   burstEnd–end:        ease-out from overshoot to 1.0 (damped settle)
// The shader reads this signed value and applies (1 + bonus * progress)
// across radius/edge-noise/4th-lobe/ink-weight/red-mix, so a single curve
// drives all five effects through dip, burst, and settle.

// Signed morph progress for the CURRENT tier-up animation. 0 at tier 1;
// 1 once settled at any tier ≥ 2 (shader keeps the tier-2 look for 3+ until
// the Heck flourish pass). Passes through dip → overshoot → settle mid-morph.
export function tierMorphProgress(player: Entity): number {
  const tier = currentTier(player)
  const t = player.evolution?.morphTimer ?? 0
  if (t <= 0) return tier >= 2 ? 1 : 0
  const u = 1 - t / T.evolution.morphDuration
  const { anticipationEnd, burstEnd, anticipationDepth, overshoot } = T.evolution

  if (u < anticipationEnd) {
    const k = u / anticipationEnd
    return -anticipationDepth * (k * k)
  }
  if (u < burstEnd) {
    const k = (u - anticipationEnd) / (burstEnd - anticipationEnd)
    const ease = 1 - (1 - k) * (1 - k)
    return -anticipationDepth + (overshoot - -anticipationDepth) * ease
  }
  const k = (u - burstEnd) / (1 - burstEnd)
  const ease = 1 - (1 - k) * (1 - k)
  return overshoot + (1.0 - overshoot) * ease
}

// Radius multiplier from the tier ladder, morph-animated: mid-morph it eases
// from the previous tier's cumulative bonus toward the new one along the
// signed dip/overshoot curve.
export function tierRadiusMult(player: Entity): number {
  const tier = currentTier(player)
  const target = TIER_SPECS[tier].radiusBonus
  const morphing = (player.evolution?.morphTimer ?? 0) > 0
  if (!morphing) return 1 + target
  const prev = TIER_SPECS[Math.max(1, tier - 1) as Tier].radiusBonus
  return 1 + prev + (target - prev) * tierMorphProgress(player)
}

// Flourish scalars for the shader (red ink-mix, horns, tail) — the descent
// toward Heck. Settled values come from T.tierFlourish; mid-morph they ride
// the signed dip/overshoot curve from the previous tier's values, so horns
// pop in with the same bounce as the body.
export interface TierFlourish {
  red: number
  horns: number
  tail: number
}

export function flourishAt(tier: Tier): TierFlourish {
  const f = T.tierFlourish
  const red = [0, 0, f.red2, f.red3, f.red4, f.red5, f.red6][tier]
  const horns = [0, 0, 0, f.horns3, f.horns4, f.horns5, f.horns6][tier]
  const tail = [0, 0, 0, 0, f.tail4, f.tail5, f.tail6][tier]
  return { red, horns, tail }
}

export function tierFlourish(player: Entity): TierFlourish {
  const tier = currentTier(player)
  const target = flourishAt(tier)
  const morphing = (player.evolution?.morphTimer ?? 0) > 0
  if (!morphing) return target
  const prev = flourishAt(Math.max(1, tier - 1) as Tier)
  const t = tierMorphProgress(player)
  return {
    red: prev.red + (target.red - prev.red) * t,
    horns: prev.horns + (target.horns - prev.horns) * t,
    tail: prev.tail + (target.tail - prev.tail) * t,
  }
}

// Form growth for the speciated critter — the tier 3→6 elaboration scalar
// (0 at speciation, 1 at max tier). Rides the same morph-blend as the old
// flourish ladder so each tier-up animates a visible growth spurt with the
// body's dip/overshoot bounce.
export function growthAt(tier: Tier): number {
  return Math.min(1, Math.max(0, (tier - 3) / 3))
}

export function formGrowth(player: Entity): number {
  const tier = currentTier(player)
  const target = growthAt(tier)
  const morphing = (player.evolution?.morphTimer ?? 0) > 0
  if (!morphing) return target
  const prev = growthAt(Math.max(1, tier - 1) as Tier)
  return prev + (target - prev) * tierMorphProgress(player)
}

// Set the player's tier. With { animate: true } and a tier increase, kicks
// off the 2-second morph timer; otherwise (load-from-IDB, manual sets in
// tests) the visuals snap to the new tier.
export function applyTier(player: Entity, tier: Tier, opts: { animate: boolean }): void {
  const wasTier = currentTier(player)
  player.evolution ??= { tier, morphTimer: 0 }
  player.evolution.tier = tier
  player.evolution.morphTimer = opts.animate && tier > wasTier ? T.evolution.morphDuration : 0
  player.evolution.burstPuffed = false
  player.evolution.startAnnounced = false
}
